import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flag, Trash2, CheckCircle2, RotateCcw, MapPin, Monitor, Clock, User, Film, XCircle, Mail, Bot, MessageSquare, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { API, BulkBar, BulkCheck } from './shared';
import { ConfirmModal } from './TrashTab';

const fmtTc = (secs) => {
  const v = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(v / 3600), m = Math.floor((v % 3600) / 60), s = v % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export function ReportsTab({ theme }) {
  const light = theme === 'light';
  const [reports, setReports] = useState([]);
  const [selectedR, setSelectedR] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [confirmDel, setConfirmDel] = useState(null);
  const [msgModal, setMsgModal] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [aiLaunching, setAiLaunching] = useState(null);
  const [openThread, setOpenThread] = useState(null);
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const hintCls = light ? 'text-gray-400' : 'text-white/35';
  const txtCls = light ? 'text-gray-800' : 'text-white';

  const load = useCallback(() => {
    axios.get(`${API}/admin/reports`, { withCredentials: true })
      .then(r => setReports(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await axios.put(`${API}/admin/reports/${id}`, { status }, { withCredentials: true }).catch(() => {});
    load();
  };
  const doDelete = async () => {
    const id = confirmDel.id; setConfirmDel(null);
    await axios.delete(`${API}/admin/reports/${id}`, { withCredentials: true }).catch(() => {});
    load();
  };
  const sendMessage = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/reports/${msgModal.id}/message`, { message: msgText.trim() }, { withCredentials: true });
      setMsgModal(null); setMsgText(''); load();
    } catch { alert("Échec de l'envoi du message."); }
    setSending(false);
  };
  const launchAI = async (id) => {
    setAiLaunching(id);
    try {
      await axios.post(`${API}/admin/reports/${id}/ai-contact`, {}, { withCredentials: true });
      load();
    } catch { alert("L'IA n'a pas pu envoyer le message."); }
    setAiLaunching(null);
  };

  const shown = reports.filter(r => filter === 'all' || r.status === filter);
  const newCount = reports.filter(r => r.status === 'new').length;

  return (
    <div data-testid="reports-tab">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <h1 className={`text-2xl font-bold ${txtCls}`}>Signalements ({reports.length})
          {newCount > 0 && <span className="ml-2 align-middle text-xs font-bold bg-[#E9641A] text-white px-2 py-0.5 rounded-full">{newCount} nouveau{newCount > 1 ? 'x' : ''}</span>}
        </h1>
        <div className="flex gap-1.5">
          {[['all', 'Tous'], ['new', 'Nouveaux'], ['resolved', 'Traités'], ['incorrect', 'Incorrects']].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`reports-filter-${v}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${filter === v ? 'bg-[#E9641A] text-white border-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className={`text-xs mb-6 ${hintCls}`}>Problèmes signalés depuis le lecteur (web, TV, mobile). L'IA contacte automatiquement l'utilisateur par email (+ push) dès réception, gère l'échange et clôt le signalement quand l'utilisateur confirme par écrit que c'est résolu.</p>

      {loading ? (
        <p className={`text-center text-sm py-10 ${hintCls}`}>Chargement...</p>
      ) : shown.length === 0 ? (
        <div className={`rounded-xl border p-10 text-center ${cardCls}`} data-testid="reports-empty-state">
          <Flag size={28} className={`mx-auto mb-3 ${light ? 'text-gray-300' : 'text-white/15'}`} />
          <p className={`text-sm ${hintCls}`}>Aucun signalement{filter !== 'all' ? ' dans ce filtre' : ' pour le moment'}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <BulkBar items={shown} selected={selectedR} setSelected={setSelectedR} kind="reports" light={light} onDone={() => { setSelectedR([]); load(); }} />
          {shown.map(r => (
            <div key={r.id} className={`rounded-xl border p-4 ${cardCls} ${r.status === 'new' ? 'border-l-4 border-l-[#E9641A]' : ''}`} data-testid={`report-item-${r.id}`}>
              <div className="flex items-start gap-2">
              <BulkCheck id={r.id} selected={selectedR} setSelected={setSelectedR} light={light} />
              <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.status === 'new' ? 'bg-[#E9641A] text-white' : r.status === 'incorrect' ? 'bg-gray-500/20 text-gray-400' : 'bg-green-500/15 text-green-500'}`}>
                      {r.status === 'new' ? 'Nouveau' : r.status === 'incorrect' ? 'Incorrect' : 'Traité'}
                    </span>
                    <span className={`text-sm font-bold ${txtCls}`}>{r.category_label || r.category}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${light ? 'bg-gray-100 text-gray-500' : 'bg-white/5 text-white/40'}`}>{r.platform === 'tv' ? 'TV' : r.platform === 'mobile' ? 'Mobile' : 'Web'}</span>
                    {r.ai_status && (
                      <span data-testid={`report-ai-status-${r.id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${r.ai_status === 'resolved' ? 'bg-green-500/15 text-green-500' : r.ai_status === 'escalated' ? 'bg-red-500/15 text-red-400' : 'bg-purple-500/15 text-purple-400'}`}>
                        <Bot size={10} /> {r.ai_status === 'resolved' ? "IA — résolu (confirmé par l'utilisateur)" : r.ai_status === 'escalated' ? 'IA — escaladé, intervention requise' : 'IA — en attente de réponse'}
                      </span>
                    )}
                  </div>
                  <div className={`flex flex-wrap gap-x-4 gap-y-1 text-[11px] mb-2 ${hintCls}`}>
                    <span className="flex items-center gap-1"><User size={11} /> {r.user_name} ({r.user_email}){r.user_plan ? ` · ${r.user_plan}` : ''}{r.profile_name ? ` · profil « ${r.profile_name} »` : ''}</span>
                    <span className="flex items-center gap-1"><Clock size={11} /> {new Date(r.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                    {r.location && <span className="flex items-center gap-1"><MapPin size={11} /> {r.location}</span>}
                    {r.device && <span className="flex items-center gap-1"><Monitor size={11} /> {r.device}</span>}
                  </div>
                  <p className={`text-xs mb-1 ${txtCls}`}>
                    <Film size={11} className="inline mr-1 -mt-0.5" />
                    <span className="font-semibold">{r.content_title || 'Contenu inconnu'}</span>
                    {r.episode_label && <span> · {r.episode_label}</span>}
                    <span className={hintCls}> · drapeau enclenché à {fmtTc(r.timecode)}</span>
                  </p>
                  {(r.reasons || []).length > 0 && (
                    <ul className={`text-xs mt-2 space-y-0.5 ${light ? 'text-gray-600' : 'text-white/65'}`}>
                      {r.reasons.map((reason, i) => <li key={i}>• {reason}</li>)}
                    </ul>
                  )}
                  {(r.filter_type || r.filter_timecode) && (
                    <p className="text-xs mt-2 text-[#E9641A] font-semibold">
                      Filtre : {r.filter_type || 'non précisé'}{r.filter_timecode ? ` · au timecode ${r.filter_timecode}` : ''}
                    </p>
                  )}
                  {r.details && (
                    <p className={`text-xs mt-2 rounded-lg px-3 py-2 italic ${light ? 'bg-gray-50 text-gray-600' : 'bg-white/[0.04] text-white/60'}`}>« {r.details} »</p>
                  )}
                  {(r.ai_thread || []).length > 0 && (
                    <div className="mt-2">
                      <button onClick={() => setOpenThread(openThread === r.id ? null : r.id)} data-testid={`report-thread-toggle-${r.id}`}
                        className="flex items-center gap-1 text-[11px] font-semibold text-purple-400 hover:text-purple-300 transition-colors">
                        <MessageSquare size={11} /> Conversation ({r.ai_thread.length} message{r.ai_thread.length > 1 ? 's' : ''})
                        {openThread === r.id ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                      </button>
                      {openThread === r.id && (
                        <div className="mt-2 space-y-2" data-testid={`report-thread-${r.id}`}>
                          {r.ai_thread.map((m, i) => (
                            <div key={i} className={`rounded-lg px-3 py-2 text-xs ${m.role === 'user' ? (light ? 'bg-gray-100 text-gray-700' : 'bg-white/[0.06] text-white/75') : m.role === 'admin' ? 'bg-blue-500/10 text-blue-300' : 'bg-purple-500/10 text-purple-300'}`}>
                              <p className="text-[9px] font-bold uppercase mb-1 opacity-70">
                                {m.role === 'user' ? 'Utilisateur' : m.role === 'admin' ? 'Vous (admin)' : 'IA Maya+'} · {m.at ? new Date(m.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}{m.channel === 'email+push' ? ' · email + push' : ''}
                              </p>
                              <p className="whitespace-pre-wrap">{m.text}</p>
                            </div>
                          ))}
                          {r.ai_summary && <p className={`text-[10px] italic ${hintCls}`}>Résumé IA : {r.ai_summary}</p>}
                        </div>
                      )}
                    </div>
                  )}
                  {r.email_status && (
                    <p className={`text-[10px] mt-2 flex items-center gap-1 font-semibold ${r.email_status === 'sent' ? 'text-green-500' : 'text-red-500'}`} data-testid={`report-email-status-${r.id}`}>
                      <Mail size={11} /> {r.email_status === 'sent' ? "E-mail envoyé à l'utilisateur" : `Échec de l'envoi de l'e-mail${r.email_error ? ` — ${r.email_error}` : ''}`}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 flex-none">
                  {r.user_email && (
                    <button onClick={() => setMsgModal(r)} data-testid={`report-contact-${r.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-blue-600/90 hover:bg-blue-500 text-white transition-colors">
                      <Mail size={11} /> Contacter
                    </button>
                  )}
                  {r.user_email && !(r.ai_thread || []).length && (
                    <button onClick={() => launchAI(r.id)} disabled={aiLaunching === r.id} data-testid={`report-ai-launch-${r.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-purple-600/90 hover:bg-purple-500 text-white transition-colors disabled:opacity-50">
                      <Bot size={11} /> {aiLaunching === r.id ? 'Envoi IA…' : "Lancer l'IA"}
                    </button>
                  )}
                  {r.status === 'new' ? (
                    <>
                      <button onClick={() => setStatus(r.id, 'resolved')} data-testid={`report-resolve-${r.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-green-600/90 hover:bg-green-500 text-white transition-colors">
                        <CheckCircle2 size={11} /> Marquer traité
                      </button>
                      <button onClick={() => setStatus(r.id, 'incorrect')} data-testid={`report-incorrect-${r.id}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${light ? 'border-gray-300 text-gray-500 hover:border-gray-500 hover:text-gray-700' : 'border-white/15 text-white/55 hover:border-white/40 hover:text-white'}`}>
                        <XCircle size={11} /> Marquer incorrect
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setStatus(r.id, 'new')} data-testid={`report-reopen-${r.id}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>
                      <RotateCcw size={11} /> Rouvrir
                    </button>
                  )}
                  <button onClick={() => setConfirmDel(r)} data-testid={`report-delete-${r.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-red-600/90 hover:bg-red-500 text-white transition-colors">
                    <Trash2 size={11} /> Supprimer
                  </button>
                </div>
              </div>
              </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {msgModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="report-message-modal">
          <div className={`w-full max-w-lg rounded-2xl border p-5 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`}>
            <h3 className={`text-sm font-bold mb-1 ${txtCls}`}>Contacter {msgModal.user_name || msgModal.user_email}</h3>
            <p className={`text-[11px] mb-3 ${hintCls}`}>Envoyé par email à {msgModal.user_email} (+ push mobile si l'app est installée). Sa réponse sera ensuite prise en charge par l'IA.</p>
            <textarea value={msgText} onChange={e => setMsgText(e.target.value)} rows={6} data-testid="report-message-input"
              placeholder="Bonjour, pouvez-vous nous préciser…"
              className={`w-full rounded-lg border p-3 text-xs outline-none resize-none ${light ? 'bg-gray-50 border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`} />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setMsgModal(null); setMsgText(''); }} data-testid="report-message-cancel"
                className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border ${light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>Annuler</button>
              <button onClick={sendMessage} disabled={sending || !msgText.trim()} data-testid="report-message-send"
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[11px] font-semibold bg-[#E9641A] hover:bg-[#f0763a] text-white transition-colors disabled:opacity-50">
                <Send size={11} /> {sending ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <ConfirmModal light={light} title="Supprimer ce signalement ?"
          message={`Le signalement de ${confirmDel.user_name || 'cet utilisateur'} sur « ${confirmDel.content_title} » sera supprimé définitivement.`}
          confirmLabel="Supprimer" onConfirm={doDelete} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}
